<template>
  <Widget :headline="$t('careLevelCalculator.analysis.pflegegradeinstufung.careLevel')">
    <InViewAnimation :delay="5">
      <div slot="0">
        <CareLevelBar :class="`${el}__careLevelBar`" vertical v-bind="careLevelBarProps" />
      </div>
    </InViewAnimation>

    <template slot="bottom">
      <template v-for="(item, i) in bottomLink">
        <Button
          :key="`button-${i}`"
          v-if="item.isButton"
          v-bind="item"
          additionalIconName="arrow-right-line"
          additionalIconPos="right"
          small
          @click="$emit('onTrackingClick', '_btn-' + item.label, 'button')"
        />
        <Link
          v-else
          :key="`link-${i}`"
          :to="item.href"
          :onClick="item.onClick"
          icon="arrow-right-line"
          iconPos="right"
        >
          {{ item.label }}
        </Link>
      </template>
    </template>
  </Widget>
</template>

<script>
import Widget from '@/objects/Widget';
import Button from '@/objects/Button';
import Link from '@/objects/Link';
import CareLevelBar from '@/components/CareLevelBar';
import InViewAnimation from '@/components/InViewAnimation';
import careLevelMixin from '@/mixins/careLevel';

import { formatDate } from '@/helpers/formatter';

import { mapFieldsAuth, mapFieldsHealth, mapFieldsTask } from '@/store/mapFields';

import { ROUTE_SERVICE_CARELEVEL_CALCULATOR } from '@/constants/routes';

import { HEALTH_TOGGLE_CARE_LEVEL_MODAL } from '@/store/actions/health';

export default {
  name: 'CareLevelCalculatorAnalysis',
  props: {},
  mixins: [careLevelMixin],
  components: {
    InViewAnimation,
    Widget,
    CareLevelBar,
    Button,
    Link,
  },

  computed: {
    ...mapFieldsAuth(['isAuthenticated']),
    ...mapFieldsHealth(['careLevel', 'careLevelDate', 'healthUuid', 'careLevelSituation']),
    ...mapFieldsTask(['completedTasks']),

    careLevelBarProps() {
      const pflegegrad = this.calculatedCareLevel;

      // Default: Kein Pflegegrad und kein Errechneter -> Figma Vatiante 1
      let props = {
        centeredCareLevel: true,
        subline: 'Es liegt aktuell kein Pflegegrad vor.',
      };

      // Vermutlich kein PG -> Figma Vatiante 2
      if (pflegegrad === 0 && this.careLevelSituation === 2) {
        props = {
          centeredCareLevel: true,
          subline: 'Es besteht vermutlich kein Pflegegrad.',
          careLevelColor: 'primaryCoral',
        };
        this.toggleCtaLabel(1);
      }

      // Hat Pflegegrad beantragt ohne Datum -> Figma Vatiante 3/4
      if (this.careLevelSituation === 1 && this.careLevel && !this.careLevelDate) {
        props = {
          centeredCareLevel: true,
          careLevel: this.careLevel,
          subline: 'Beantragter Pflegegrad',
          careLevelColor: 'primaryCoral',
        };
        this.toggleCtaLabel(1);
      }

      // Hat nur errechneten Pflegegrad -> Figma Vatiante 3
      if (pflegegrad && pflegegrad > 0 && this.careLevelSituation === 2 && !this.careLevel) {
        props = {
          centeredCareLevel: true,
          careLevel: pflegegrad,
          subline: 'Geschätzter Pflegegrad aufgrund Ihrer Angaben.',
          careLevelColor: 'primaryCoral',
        };
        this.toggleCtaLabel(1);
      }

      // Hat Pflegegrad beantragt mit Datum -> Figma Vatiante 4
      if (this.careLevel && this.careLevelDate && this.careLevelSituation === 1) {
        props = {
          centeredCareLevel: true,
          careLevel: this.careLevel,
          subline: `Pflegegrad beantragt am ${formatDate(this.careLevelDate)}`,
          careLevelColor: 'primaryCoral',
        };
        this.toggleCtaLabel(1);
      }

      // Hat Pflegegrad ohne Datum -> Figma Vatiante 6
      if (this.careLevelSituation === 0 && this.careLevel && !this.careLevelDate) {
        props = {
          careLevel: this.careLevel,
          subline: 'Seit wann besteht der Pflegegrad?',
          careLevelColor: 'secondaryMintLight',
        };
        this.toggleCtaLabel(2);
      }

      // Hat Pflegegrad mit Datum -> Figma Vatiante 5
      if (this.careLevelSituation === 0 && this.careLevel && this.careLevelDate) {
        props = {
          careLevel: this.careLevel,
          subline: `Seit ${formatDate(this.careLevelDate)} von der Pflegekasse anerkannt.`,
          careLevelColor: 'secondaryMintLight',
        };
        this.toggleCtaLabel(2);
      }

      // Hat Pflegegrad und hat berechneten Pflegegrad, der höher ist -> Figma Vatiante 9
      if (
        this.careLevel &&
        this.careLevelSituation === 0 &&
        pflegegrad &&
        pflegegrad > 0 &&
        pflegegrad > this.careLevel
      ) {
        props = {
          careLevel: this.careLevel,
          calculatedCareLevel: pflegegrad,
          subline: 'Geschätzter Pflegegrad aufgrund Ihrer erneuten Berechnung.',
          careLevelColor: 'primaryCoral',
        };
        this.toggleCtaLabel(2);
      }

      // TODO: careLevelDate und pflegegradDatum haben eine Differenz von X Monaten mit in die Condition aufnehmen
      // Hat Pflegegrad, der älter ist als X Monate (kein PG berechnet).
      // Aktueller Pflegegrad (nicht mehr aktuell) -> Figma Variante 8
      // if (this.careLevel && this.careLevelSituation === 0 && !pflegegrad) {
      //   props = {
      //     careLevel: this.careLevel,
      //     calculatedCareLevel: '?',
      //     reverseSquareSize: true,
      //     subline: 'Haben Sie noch den richtigen Pflegegrad? Prüfen Sie diesen erneut.',
      //     careLevelColor: 'primaryCoral',
      //   };
      //   this.toggleCtaLabel(2);
      // }

      return props;
    },
  },
  data() {
    return {
      formatDate,
      trackingLinkIsClicked: null,
      lastRoute: null,
      bottomLink: [
        {
          isButton: true,
          label: this.$t('careLevelCalculator.widget.button1.label'),
          type: 'outlined',
          onClick: () => this.openModal(),
        },
        {
          label: this.$t('careLevelCalculator.widget.link.label'),
          href: ROUTE_SERVICE_CARELEVEL_CALCULATOR,
          onClick: () =>
            this.$emit(
              'onTrackingClick',
              '_link-' + this.$t('careLevelCalculator.widget.link.label'),
              'link'
            ),
        },
      ],
      el: 'v-careLevelCalculatorAnalysisWizard',
      classes: () => {
        const el = this.el;
        return {
          [el]: true,
        };
      },
    };
  },
  methods: {
    toggleCtaLabel(i = 1) {
      this.bottomLink[0].label = this.$t(`careLevelCalculator.widget.button${i}.label`);
    },

    openModal() {
      this.$store.dispatch(HEALTH_TOGGLE_CARE_LEVEL_MODAL);
    },
  },
};
</script>
